import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as ReactToastify from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { useGetProductCategoriesQuery } from '../../redux/productCategoryApi';
import { useGetProductSubcategoriesQuery } from '../../redux/productSubcategoryApi';
import { useGetProductsQuery, useUpdateProductMutation } from '../../redux/productApi';
import {
  IMAGE_MAX_KB,
  PRODUCT_NAME_MAX_LEN,
  PRODUCT_SPS_MAX,
  clampPercentInput,
  sanitizeNonNegativeDecimal,
  sanitizeUnitInput,
  validateImageFilesUnderSize,
  validateOptionalAlphaNumeric,
  validateOptionalNonNegativeNumber,
  validateOptionalPercent,
  validateOptionalUnit,
  validateProductName,
  validateRequired,
} from '../../utils/validation';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { AuthorizedMedia } from '../AuthorizedMedia';

function toInputDate(val) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return '';
}

function resolveCategoryIdFromProduct(rawProduct, categoriesList) {
  if (!rawProduct) return '';
  const id =
    rawProduct.product_category_id ??
    rawProduct.productCategoryId ??
    (rawProduct.product_category && typeof rawProduct.product_category === 'object'
      ? rawProduct.product_category.id
      : null) ??
    rawProduct.category_id ??
    rawProduct.categoryId ??
    (rawProduct.category && typeof rawProduct.category === 'object'
      ? rawProduct.category.id
      : null);
  if (id != null && id !== '') return String(id);

  const name =
    rawProduct.product_category_name ||
    rawProduct.productCategoryName ||
    (rawProduct.product_category && typeof rawProduct.product_category === 'object'
      ? rawProduct.product_category.name
      : '') ||
    rawProduct.category_name ||
    rawProduct.categoryName ||
    (typeof rawProduct.category === 'string' ? rawProduct.category : '') ||
    (rawProduct.category && typeof rawProduct.category === 'object'
      ? rawProduct.category.name
      : '') ||
    '';
  if (!name || !Array.isArray(categoriesList)) return '';
  const nameLower = String(name).trim().toLowerCase();
  const found = categoriesList.find(
    (c) => String(c.name || '').trim().toLowerCase() === nameLower
  );
  return found != null && found.id != null ? String(found.id) : '';
}

function pickSubcategoryIdDirect(rawProduct) {
  if (!rawProduct) return '';
  const id =
    rawProduct.product_subcategory_id ??
    rawProduct.productSubcategoryId ??
    (rawProduct.product_subcategory && typeof rawProduct.product_subcategory === 'object'
      ? rawProduct.product_subcategory.id
      : null) ??
    rawProduct.subcategory_id ??
    rawProduct.subcategoryId ??
    rawProduct.sub_category_id ??
    (rawProduct.subcategory && typeof rawProduct.subcategory === 'object'
      ? rawProduct.subcategory.id
      : null);
  return id != null && id !== '' ? String(id) : '';
}

function resolveSubcategoryIdFromProduct(rawProduct, subcategoriesList) {
  const direct = pickSubcategoryIdDirect(rawProduct);
  if (direct) return direct;
  if (!rawProduct || !Array.isArray(subcategoriesList)) return '';
  const name =
    rawProduct.product_subcategory_name ||
    rawProduct.productSubcategoryName ||
    (rawProduct.product_subcategory && typeof rawProduct.product_subcategory === 'object'
      ? rawProduct.product_subcategory.name
      : '') ||
    rawProduct.subcategory_name ||
    rawProduct.subcategoryName ||
    (typeof rawProduct.subcategory === 'string' ? rawProduct.subcategory : '') ||
    (rawProduct.subcategory && typeof rawProduct.subcategory === 'object'
      ? rawProduct.subcategory.name
      : '') ||
    '';
  if (!name) return '';
  const lower = String(name).trim().toLowerCase();
  const found = subcategoriesList.find(
    (s) => String(s.name || '').trim().toLowerCase() === lower
  );
  return found != null && found.id != null ? String(found.id) : '';
}

function pickProductText(rawProduct, keys) {
  if (!rawProduct) return '';
  for (const key of keys) {
    const value = rawProduct[key];
    if (value != null && value !== '') return String(value);
  }
  return '';
}

function pickProductPercent(rawProduct, keys) {
  return String(pickProductText(rawProduct, keys) || '')
    .trim()
    .replace(/%/g, '');
}

function AdminProductEdit() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = Number(id);

  const { data: productsRes, isFetching } = useGetProductsQuery();
  const { data: categoriesRes } = useGetProductCategoriesQuery({
    per_page: 500,
    status: true,
  });
  const [updateProduct, { isLoading }] = useUpdateProductMutation();

  const rawProduct = useMemo(() => {
    const list = productsRes?.data || productsRes || [];
    if (!Array.isArray(list)) return null;
    return list.find((p) => Number(p?.id) === productId) || null;
  }, [productsRes, productId]);

  const categoryOptions = useMemo(() => {
    const list = (categoriesRes?.data || []).map((cat) => ({
      value: String(cat.id),
      label: cat.name ?? String(cat.id),
    }));

    const currentId =
      rawProduct?.product_category_id ??
      rawProduct?.productCategoryId ??
      rawProduct?.category_id ??
      rawProduct?.categoryId;
    const currentName =
      rawProduct?.product_category_name ??
      rawProduct?.productCategoryName ??
      rawProduct?.product_category?.name ??
      rawProduct?.category_name ??
      rawProduct?.categoryName;
    if (currentId != null && currentId !== '' && currentName) {
      const idStr = String(currentId);
      const exists = list.some((o) => String(o.value) === idStr);
      if (!exists) list.unshift({ value: idStr, label: currentName });
    }

    return list;
  }, [categoriesRes, rawProduct]);

  const [form, setForm] = useState({
    name: '',
    category: '',
    subCategory: '',
    batchNo: '',
    mfgDate: '',
    bestBefore: '',
    buffer: '',
    unit: '',
    itemCategory: '',
    sps1: '',
    sps2: '',
    sps3: '',
    itemCode: '',
    status: true,
  });
  const { data: subcategoriesRes } = useGetProductSubcategoriesQuery(
    {
      product_category_id: form.category,
      status: true,
      per_page: 500,
    },
    {
      skip: !form.category,
    }
  );

  const subCategoryOptions = useMemo(() => {
    const list = (subcategoriesRes?.data || []).map((sub) => ({
      value: String(sub.id),
      label: sub.name ?? String(sub.id),
    }));

    const currentId =
      rawProduct?.product_subcategory_id ??
      rawProduct?.productSubcategoryId ??
      rawProduct?.subcategory_id ??
      rawProduct?.subcategoryId;
    const currentName =
      rawProduct?.product_subcategory_name ??
      rawProduct?.productSubcategoryName ??
      rawProduct?.product_subcategory?.name ??
      rawProduct?.subcategory_name ??
      rawProduct?.subcategoryName;
    if (currentId != null && currentId !== '' && currentName) {
      const idStr = String(currentId);
      const exists = list.some((o) => String(o.value) === idStr);
      if (!exists) list.unshift({ value: idStr, label: currentName });
    }

    return list;
  }, [subcategoriesRes, rawProduct]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const imageInputRef = useRef(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const lastSyncedProductIdRef = useRef(null);
  const subNameResolvedRef = useRef(false);
  const [errors, setErrors] = useState({});

  const existingImageUrls = useMemo(() => {
    if (!rawProduct) return [];
    const list = [];
    const pushResolved = (v) => {
      const u = resolveAssetUrl(v);
      if (u) list.push(u);
    };

    const imageArrays = [
      rawProduct.images,
      rawProduct.product_images,
      rawProduct.image_urls,
      rawProduct.item_images,
      rawProduct.pictures,
      rawProduct.photos,
      rawProduct.image_attachments,
    ].filter(Array.isArray);

    imageArrays.forEach((rawImages) => {
      rawImages.forEach((img) => {
        if (!img) return;
        if (typeof img === 'string') {
          pushResolved(img);
          return;
        }
        pushResolved(img);
      });
    });

    [
      rawProduct.image_url,
      rawProduct.imageUrl,
      rawProduct.image,
      rawProduct.image_url_path,
      rawProduct.primary_image,
      rawProduct.cover_image,
      rawProduct.thumbnail,
    ].forEach(pushResolved);

    return [...new Set(list.filter(Boolean))];
  }, [rawProduct]);

  useEffect(() => {
    if (!rawProduct) return;
    if (lastSyncedProductIdRef.current !== rawProduct.id) {
      lastSyncedProductIdRef.current = rawProduct.id;
      subNameResolvedRef.current = false;
    }
    const status = rawProduct.status ?? true;
    const categoriesList = categoriesRes?.data;
    const categoryId = resolveCategoryIdFromProduct(rawProduct, categoriesList);
    const subDirect = pickSubcategoryIdDirect(rawProduct);
    setForm({
      name: rawProduct.item_name || rawProduct.name || rawProduct.itemName || '',
      category: categoryId,
      subCategory: subDirect,
      batchNo: rawProduct.batch_number || rawProduct.batchNo || '',
      mfgDate: toInputDate(rawProduct.mfg_date || rawProduct.mfgDate),
      bestBefore: toInputDate(rawProduct.best_before || rawProduct.bestBefore),
      buffer: pickProductPercent(rawProduct, ['buffer']),
      unit: sanitizeUnitInput(pickProductText(rawProduct, ['unit'])),
      itemCategory: pickProductPercent(rawProduct, ['item_category', 'itemCategory']),
      sps1: pickProductText(rawProduct, ['sps_1', 'sps1']),
      sps2: pickProductText(rawProduct, ['sps_2', 'sps2']),
      sps3: pickProductText(rawProduct, ['sps_3', 'sps3']),
      itemCode: pickProductText(rawProduct, ['item_code', 'itemCode']),
      status: status === true || status === 'true' || status === 1,
    });
    setImageFiles([]);
    setActiveImageIndex(0);
    if (imageInputRef.current) imageInputRef.current.value = '';
  }, [rawProduct, categoriesRes]);

  useEffect(() => {
    if (!rawProduct || !form.category) return;
    if (pickSubcategoryIdDirect(rawProduct)) return;
    if (subNameResolvedRef.current) return;
    const subs = subcategoriesRes?.data;
    if (!Array.isArray(subs) || subs.length === 0) return;
    const resolved = resolveSubcategoryIdFromProduct(rawProduct, subs);
    if (!resolved) return;
    subNameResolvedRef.current = true;
    setForm((prev) => ({ ...prev, subCategory: resolved }));
  }, [rawProduct, form.category, subcategoriesRes]);

  useEffect(() => {
    if (imageFiles && imageFiles.length > 0) {
      const urls = imageFiles.map((f) => URL.createObjectURL(f));
      setImagePreviewUrls(urls);
      setActiveImageIndex(0);
      return () => urls.forEach((u) => URL.revokeObjectURL(u));
    }
    setImagePreviewUrls([]);
    return undefined;
  }, [imageFiles]);

  const displayImages =
    imagePreviewUrls.length > 0 ? imagePreviewUrls : existingImageUrls;
  const displayImageUrl =
    displayImages[activeImageIndex] || displayImages[0] || null;

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => {
      if (field === 'category') {
        return { ...prev, category: value, subCategory: '' };
      }
      if (field === 'batchNo') {
        return {
          ...prev,
          batchNo: String(value ?? '')
            .toUpperCase()
            .replace(/[^A-Za-z0-9]/g, ''),
        };
      }
      if (field === 'buffer' || field === 'itemCategory') {
        return { ...prev, [field]: clampPercentInput(value) };
      }
      if (field === 'unit') {
        return { ...prev, unit: sanitizeUnitInput(value) };
      }
      if (field === 'sps1' || field === 'sps2' || field === 'sps3') {
        return { ...prev, [field]: sanitizeNonNegativeDecimal(value) };
      }
      return { ...prev, [field]: value };
    });
    setErrors((prev) => {
      if (field !== 'category') return { ...prev, [field]: '' };
      return { ...prev, category: '', subCategory: '' };
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const err = validateImageFilesUnderSize(files);
    if (err) {
      ReactToastify.toast.error(err);
      if (imageInputRef.current) imageInputRef.current.value = '';
      setImageFiles([]);
      setErrors((prev) => ({ ...prev, images: err }));
      return;
    }
    setImageFiles(files);
    setErrors((prev) => ({ ...prev, images: '' }));
  };

  const handleRemoveNewImages = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageFiles([]);
    setErrors((prev) => ({ ...prev, images: '' }));
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const selectFieldClass =
    'rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  const reqLabel = (text) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <span className="min-w-0 leading-snug">{text}</span>
      <span className="shrink-0 text-base leading-none text-red-600" aria-hidden="true">
        *
      </span>
    </span>
  );

  const validateForm = () => {
    const next = {};

    const nameErr = validateProductName(form.name);
    if (nameErr) next.name = nameErr;

    const catErr = validateRequired(form.category, 'Product Category');
    if (catErr) next.category = catErr;

    const subErr = validateRequired(form.subCategory, 'Product Sub Category');
    if (subErr) next.subCategory = subErr;

    const batchErr = validateOptionalAlphaNumeric(form.batchNo, 'Batch No.');
    if (batchErr) next.batchNo = batchErr;

    const mfgErr = validateRequired(form.mfgDate, 'MFG Date');
    if (mfgErr) next.mfgDate = mfgErr;

    const bestErr = validateRequired(form.bestBefore, 'Use Before / Best Before');
    if (bestErr) next.bestBefore = bestErr;

    const bufferErr = validateOptionalPercent(form.buffer, 'Buffer');
    if (bufferErr) next.buffer = bufferErr;

    const unitErr = validateOptionalUnit(form.unit);
    if (unitErr) next.unit = unitErr;

    const itemCatErr = validateOptionalPercent(form.itemCategory, 'Item Category');
    if (itemCatErr) next.itemCategory = itemCatErr;

    const sps1Err = validateOptionalNonNegativeNumber(form.sps1, 'SPS 1');
    if (sps1Err) next.sps1 = sps1Err;
    const sps2Err = validateOptionalNonNegativeNumber(form.sps2, 'SPS 2');
    if (sps2Err) next.sps2 = sps2Err;
    const sps3Err = validateOptionalNonNegativeNumber(form.sps3, 'SPS 3');
    if (sps3Err) next.sps3 = sps3Err;

    const hasAnyImage = existingImageUrls.length > 0 || imageFiles.length > 0;
    if (!hasAnyImage) {
      next.images = 'Please upload product image';
    } else if (imageFiles.length) {
      const imgErr = validateImageFilesUnderSize(imageFiles);
      if (imgErr) next.images = imgErr;
    }

    setErrors(next);
    const ok = Object.keys(next).length === 0;
    if (!ok) {
      const first = Object.values(next).find(Boolean);
      if (first) ReactToastify.toast.error(first);
    }
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await updateProduct({
        id: productId,
        item_name: String(form.name ?? '').trim(),
        product_category_id: form.category ? Number(form.category) : undefined,
        product_subcategory_id: form.subCategory ? Number(form.subCategory) : undefined,
        batch_number: form.batchNo,
        mfg_date: form.mfgDate || undefined,
        best_before: form.bestBefore || undefined,
        buffer: String(form.buffer ?? '').trim() || undefined,
        unit: String(form.unit ?? '').trim() || undefined,
        item_category: String(form.itemCategory ?? '').trim() || undefined,
        sps_1: String(form.sps1 ?? '').trim() || undefined,
        sps_2: String(form.sps2 ?? '').trim() || undefined,
        sps_3: String(form.sps3 ?? '').trim() || undefined,
        item_code: String(form.itemCode ?? '').trim() || undefined,
        status: form.status,
        images: imageFiles.length ? imageFiles : undefined,
      }).unwrap();
      ReactToastify.toast.success('Product updated successfully.');
      navigate('/dashboard/admin/products');
    } catch (err) {
      ReactToastify.toast.error(
        err?.data?.message || 'Failed to update product.'
      );
    }
  };

  if (!isFetching && !rawProduct) {
    return (
      <DashboardLayout title="Edit Product">
        <div className="space-y-4 py-4">
          <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <p className="text-sm text-gray-600">Product not found.</p>
            <Link
              to="/dashboard/admin/products"
              className="mt-4 inline-block text-sm font-semibold text-brand hover:underline"
            >
              Back to list
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Product">
      <div className="space-y-4 py-4">
        <div className="flex justify-end">
          <p className="text-sm text-gray-500">
            <Link
              to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
              className="text-brand hover:underline"
            >
              Home
            </Link>
            {' / '}
            <Link
              to="/dashboard/admin/products"
              className="text-brand hover:underline"
            >
              Product
            </Link>
            {' / '}
            <span className="text-gray-400">Edit Product</span>
          </p>
        </div>

        <form
          className="rounded-2xl bg-white p-4 shadow-sm sm:p-6"
          onSubmit={handleSubmit}
        >
 <h3 className="text-xl font-semibold text-gray-800">
              Product Information
            </h3>
          <div className="my-6 grid gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Item Code</span>
              <input
                type="text"
                placeholder="Enter item code"
                value={form.itemCode}
                onChange={handleChange('itemCode')}
                className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Item Name')}
              <input
                type="text"
                placeholder="Enter name"
                value={form.name}
                onChange={handleChange('name')}
                maxLength={PRODUCT_NAME_MAX_LEN}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.name ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.name ? (
                <span className="text-xs text-red-600">{errors.name}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Product Category')}
              <SearchableSelect
                key={`category-${(categoriesRes?.data || []).length}-${form.category}`}
                name="category"
                value={form.category}
                onChange={handleChange('category')}
                placeholder="Select"
                className={`${selectFieldClass} ${errors?.category ? 'border-red-500' : ''}`}
                options={categoryOptions}
              />
              {errors?.category ? (
                <span className="text-xs text-red-600">{errors.category}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Product Sub Category')}
              <SearchableSelect
                name="subCategory"
                value={form.subCategory}
                onChange={handleChange('subCategory')}
                placeholder="Select"
                disabled={!form.category}
                className={`${selectFieldClass} ${errors?.subCategory ? 'border-red-500' : ''}`}
                options={subCategoryOptions}
              />
              {errors?.subCategory ? (
                <span className="text-xs text-red-600">{errors.subCategory}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Batch No. (As Per Actual)</span>
              <input
                type="text"
                placeholder="Enter Batch No."
                value={form.batchNo}
                onChange={handleChange('batchNo')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.batchNo ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.batchNo ? (
                <span className="text-xs text-red-600">{errors.batchNo}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('MFG Date')}
              <input
                type="date"
                value={form.mfgDate}
                onChange={handleChange('mfgDate')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.mfgDate ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.mfgDate ? (
                <span className="text-xs text-red-600">{errors.mfgDate}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Use Before / Best Before')}
              <input
                type="date"
                value={form.bestBefore}
                onChange={handleChange('bestBefore')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.bestBefore ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.bestBefore ? (
                <span className="text-xs text-red-600">{errors.bestBefore}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Buffer (%)</span>
              <input
                type="number"
                step="any"
                min="0"
                max="100"
                placeholder="Enter buffer % (max 100)"
                value={form.buffer}
                onChange={handleChange('buffer')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.buffer ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.buffer ? (
                <span className="text-xs text-red-600">{errors.buffer}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Unit</span>
              <input
                type="text"
                placeholder="Enter unit (e.g. Kg, Nos)"
                value={form.unit}
                onChange={handleChange('unit')}
                maxLength={20}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.unit ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.unit ? (
                <span className="text-xs text-red-600">{errors.unit}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Item Category (%)</span>
              <input
                type="number"
                step="any"
                min="0"
                max="100"
                placeholder="Enter item category % (max 100)"
                value={form.itemCategory}
                onChange={handleChange('itemCategory')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.itemCategory ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.itemCategory ? (
                <span className="text-xs text-red-600">{errors.itemCategory}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 1</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 1"
                value={form.sps1}
                onChange={handleChange('sps1')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps1 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps1 ? (
                <span className="text-xs text-red-600">{errors.sps1}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 2</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 2"
                value={form.sps2}
                onChange={handleChange('sps2')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps2 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps2 ? (
                <span className="text-xs text-red-600">{errors.sps2}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 3</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 3"
                value={form.sps3}
                onChange={handleChange('sps3')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps3 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps3 ? (
                <span className="text-xs text-red-600">{errors.sps3}</span>
              ) : null}
            </label>
          </div>

          <div className="mb-6">
            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0 text-sm text-gray-700">
              {reqLabel('Product Image')}
              <span className="text-xs font-normal text-gray-500">
                (max {IMAGE_MAX_KB}KB each)
              </span>
            </div>
            <div
              className={`relative h-40 w-64 overflow-hidden rounded-xl border border-dashed bg-[#F7E5D5] ${
                errors?.images ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <label
                htmlFor="product-image-edit"
                className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
              >
                {displayImageUrl ? (
                  <>
                    <AuthorizedMedia
                      src={displayImageUrl}
                      alt="Product"
                      className="h-full w-full object-cover"
                    />
                    {displayImages.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-2 py-1 text-white"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveImageIndex(
                              (p) => (p - 1 + displayImages.length) % displayImages.length
                            );
                          }}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-2 py-1 text-white"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveImageIndex((p) => (p + 1) % displayImages.length);
                          }}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <span>
                    Click to browse
                    <br />
                    or drag &amp; drop file
                  </span>
                )}
              </label>
              <input
                id="product-image-edit"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                ref={imageInputRef}
                className="hidden"
              />
              {imagePreviewUrls?.length > 0 && (
                <button
                  type="button"
                  onClick={handleRemoveNewImages}
                  className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                  aria-label="Remove new image selection"
                  title="Remove new image selection"
                >
                  ×
                </button>
              )}
            </div>
            {imageFiles.length > 0 && (
              <p className="mt-2 text-xs text-gray-600">
                New images selected:{' '}
                <span className="font-medium">{imageFiles.length}</span> (will
                upload on save)
              </p>
            )}
            {errors?.images ? (
              <p className="mt-2 text-xs text-red-600">{errors.images}</p>
            ) : null}
          </div>

          <div className="flex justify-start gap-5 pt-4">
            <button
              type="submit"
              disabled={isLoading || isFetching}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60"
            >
              {isLoading ? 'Saving...' : 'Update'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard/admin/products')}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminProductEdit;
